import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/|\/$/g, '');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">Article Not Found</h2>
            <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist or may have been moved.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-label font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer no-underline"
            >
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-100/80 text-primary-700',
    accent: 'bg-accent-100/80 text-accent-700',
    secondary: 'bg-secondary-100/80 text-secondary-700',
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer no-underline">
                Home
              </Link>
              <span>/</span>
              <span className="text-foreground-800">{article.title}</span>
            </nav>

            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag.label}
                    className={`px-2.5 py-0.5 text-[10px] font-label font-semibold rounded-full ${colorMap[tag.color] || colorMap.primary}`}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {article.date}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </div>
                  {article.readTime}
                </div>
              </div>
            </header>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="space-y-6 text-sm md:text-base text-foreground-700 leading-relaxed">
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BlogPosting',
                    headline: article.title,
                    description: article.summary,
                    image: article.imageUrl,
                    datePublished: '2026-07-01',
                    dateModified: '2026-07-23',
                    author: {
                      '@type': 'Organization',
                      name: 'VigorBuy Spreadsheet',
                      url: 'https://vigorbuyspread.mom/',
                    },
                    publisher: {
                      '@type': 'Organization',
                      name: 'VigorBuy Spreadsheet',
                      url: 'https://vigorbuyspread.mom/',
                    },
                    mainEntityOfPage: {
                      '@type': 'WebPage',
                      '@id': `https://vigorbuyspread.mom/${slug}`,
                    },
                  }),
                }}
              />
              {article.sections.map((section, idx) => {
                if (section.type === 'heading') {
                  return (
                    <section key={idx}>
                      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                        {section.title}
                      </h2>
                    </section>
                  );
                }
                if (section.type === 'paragraph') {
                  return (
                    <section key={idx}>
                      <p>{section.text}</p>
                    </section>
                  );
                }
                if (section.type === 'list' && section.items) {
                  return (
                    <section key={idx}>
                      <ul className="space-y-2 list-disc pl-5">
                        {section.items.map((item, i) => (
                          <li key={i} className="text-sm md:text-base">{item}</li>
                        ))}
                      </ul>
                    </section>
                  );
                }
                if (section.type === 'tip-box') {
                  return (
                    <section key={idx} className="my-5 p-5 rounded-xl bg-accent-100/70 border border-accent-200/50">
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-3">
                        <div className="w-5 h-5 inline-flex items-center justify-center mr-1.5">
                          <i className="ri-lightbulb-line text-accent-600"></i>
                        </div>
                        {section.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">{section.text}</p>
                    </section>
                  );
                }
                if (section.type === 'cta') {
                  return (
                    <section key={idx} className="my-6 p-6 rounded-xl bg-primary-500 text-background-50 text-center">
                      <h3 className="text-lg md:text-xl font-heading font-bold mb-3">{section.title}</h3>
                      <p className="text-sm md:text-base opacity-90 mb-5 max-w-md mx-auto">{section.text}</p>
                      <a
                        href={section.ctaUrl || mainSiteUrl}
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-label font-semibold bg-background-50 text-primary-600 rounded-full hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer no-underline"
                      >
                        {section.ctaText || 'Buy It Now'}
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </a>
                    </section>
                  );
                }
                return null;
              })}

              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-background-200/70">
                <span className="text-xs text-foreground-500">Return to:</span>
                <Link
                  to="/"
                  className="text-xs font-label font-medium text-primary-600 hover:text-primary-700 cursor-pointer no-underline"
                >
                  Homepage
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-xs font-label font-medium text-primary-600 hover:text-primary-700 cursor-pointer no-underline"
                >
                  VigorBuy Spreadsheet Main Site
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}